import { useEffect, useRef } from 'react';
import { alpha, ListItem, ListItemProps, Stack } from '@rossum/ui/material';
import { useDocumentStore } from '../../../document-store/DocumentStore';
import { SIDEBAR_TOPBAR_HEIGHT } from '../../sidebar-topbar/SidebarTopbar';

const SCROLL_TOP_OFFSET = 100;

type MagicContainerProps = ListItemProps & {
  sidebarScrollableRef: HTMLDivElement | null;
  active: boolean;
  focused?: boolean;
  selected?: boolean;
  disabled: boolean;
  ghost?: boolean;
  focusable?: boolean;
  hasMagicLine?: boolean;
  preventScroll?: boolean;
};

export const MagicContainer = ({
  sidebarScrollableRef,
  preventScroll,
  active,
  focused,
  selected,
  disabled,
  ghost,
  focusable = true,
  hasMagicLine = true,
  children,
  ...restListItemProps
}: MagicContainerProps) => {
  const { sx: listItemSx } = restListItemProps;

  const ref = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (!preventScroll && active && ref.current && sidebarScrollableRef) {
      ref.current.style.position = 'relative';
      const { offsetTop } = ref.current;
      ref.current.style.position = 'sticky';

      sidebarScrollableRef.scrollTo({
        top: offsetTop - SCROLL_TOP_OFFSET,
        behavior: 'smooth',
      });
    }
  }, [active, preventScroll, sidebarScrollableRef]);

  const topbarIsOpen = useDocumentStore(
    state => state.sidebarState.topbarVisible
  );

  return (
    <ListItem
      {...restListItemProps}
      ref={ref}
      id={hasMagicLine && active ? 'magic-line-source' : undefined}
      data-cy={
        active
          ? 'active-annotation-sidebar-datapoint'
          : 'annotation-sidebar-datapoint'
      }
      sx={{
        ...listItemSx,
        position: active ? 'sticky' : 'relative',
        cursor: active || !focusable ? 'default' : 'pointer',
        py: active ? 1 : 0,
        // fancy, because magic
        transition: theme =>
          theme.transitions.create(
            ['padding-top', 'padding-bottom', 'background-color', 'top'],
            {
              duration: 200,
              easing: theme.transitions.easing.easeInOut,
            }
          ),
        pl: 2,
        pr: 1,
        backgroundColor: theme => theme.palette.background.paper,
        ...(active
          ? {
              backgroundImage: theme =>
                `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
              top: theme =>
                topbarIsOpen
                  ? `-${theme.spacing(1)}`
                  : `calc(-${SIDEBAR_TOPBAR_HEIGHT}px - ${theme.spacing(1)})`,
              bottom: 0,
              zIndex: 2,
            }
          : focused
            ? {
                backgroundColor: theme => theme.palette.elevation[4].background,
              }
            : {}),
        ...(active
          ? disabled
            ? {
                boxShadow: theme =>
                  `inset 4px 0 0 ${theme.palette.action.disabled}`,
              }
            : {
                boxShadow: theme =>
                  `inset 4px 0 0 ${theme.palette.primary.main}`,
              }
          : {}),
        ...(!active && focusable
          ? {
              '&:hover': {
                backgroundColor: theme => theme.palette.elevation[6].background,
              },
            }
          : {}),
        ...(selected
          ? {
              top: 0,
              zIndex: 0,
              backgroundImage: theme =>
                `linear-gradient(${alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)}, ${alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)})`,
            }
          : {}),
      }}
    >
      <Stack
        spacing={1}
        sx={{
          width: '100%',
          ...(ghost
            ? { opacity: theme => theme.palette.action.disabledOpacity }
            : {}),
          ...(!focusable ? { alignItems: 'flex-end', pb: 2 } : {}),
        }}
      >
        {children}
      </Stack>
    </ListItem>
  );
};
